import { useCallback, useEffect, useState } from 'react'
import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react'
import { api } from '@/lib/axios'
import { apiErrorText } from '@/lib/api-error'
import { bestaetige } from '@/lib/dialoge'
import { cardTitle, formCardSm, inlineInput } from '@/lib/form-styles'
import { FeldStatus, mitStand, useFeld } from '@/lib/speichern'
import { createScheme, createSchemeRow, deleteScheme, deleteSchemeRow, fetchSchemesAll, reorderSchemeRows, schemeRowUrl, schemeUrl } from '../api/calculation-pricing.api'
import { useZeilenStelle } from '../lib/kalkulation-einstellungen'
import { KOSTENARTEN, KOSTENART_KURZ, KOSTENART_TEXT } from '../lib/kalkulation-labels'
import type { CalcScheme, CalcSchemeRow } from '../types/calculation.types'
import { ZellenFeld, ZellenHaken, knopfPrimaer, tabellenKopf } from './EinstellungsBausteine'

const FEHLER = 'Das hat nicht geklappt.'

/**
 * AP-37 4.6, 10. — **Zuschlagsschemata** des Mandanten: benannte, geordnete
 * Zeilenlisten mit einem Vorgabe-Schema. Eine neue Kalkulation **kopiert** das
 * gewählte Schema — Änderungen hier wirken nur auf künftige Kalkulationen (oder
 * dort über „Schema laden").
 *
 * Gespeichert wird je Feld und je Zeile (AP-36); die Prozente nur mit
 * `calculation.cost.view` — ohne das Recht sind sie nicht zu sehen.
 */
export function SchemataEinstellungen({ darfAendern, kosten }: { darfAendern: boolean; kosten: boolean }) {
  const [liste, setListe] = useState<CalcScheme[]>([])
  const [auswahl, setAuswahl] = useState<string | null>(null)
  const [neu, setNeu] = useState('')
  const [neueZeile, setNeueZeile] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const darf = darfAendern && kosten

  const laden = useCallback(async () => {
    try {
      const l = await fetchSchemesAll()
      setListe(l)
      setAuswahl((a) => (a && l.some((s) => s.id === a) ? a : (l.find((s) => s.isDefault)?.id ?? l[0]?.id ?? null)))
    } catch (e) {
      setFehler(apiErrorText(e, FEHLER))
    }
  }, [])
  useEffect(() => {
    void laden()
  }, [laden])

  const schritt = async (tu: () => Promise<unknown>) => {
    try {
      setFehler(null)
      await tu()
      await laden()
    } catch (e) {
      setFehler(apiErrorText(e, FEHLER))
    }
  }
  const schema = liste.find((s) => s.id === auswahl) ?? null

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
      <div className={formCardSm}>
        <h3 className={cardTitle}>Zuschlagsschemata</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Eine neue Kalkulation kopiert das Vorgabe-Schema bzw. das beim Anlegen gewählte. „Standard" ab Werk ist ein Platzhalter, keine Empfehlung.
        </p>
        {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className={tabellenKopf}>
              <th className="py-1.5 pr-2">Name</th>
              <th className="py-1.5 pr-2">Vorgabe</th>
              <th className="py-1.5 pr-2">aktiv</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {liste.map((s) => (
              <SchemaZeile
                key={s.id}
                s={s}
                darf={darfAendern}
                gewaehlt={s.id === auswahl}
                onWaehlen={() => setAuswahl(s.id)}
                onVorgabe={() => void schritt(() => api.patch(schemeUrl(s.id), { isDefault: true }, mitStand(s.updatedAt)))}
                onLoeschen={async () => {
                  if (await bestaetige(`Schema „${s.name}" löschen? Kalkulationen behalten ihre Aufschläge.`, { ok: 'Löschen', gefaehrlich: true })) void schritt(() => deleteScheme(s.id))
                }}
              />
            ))}
          </tbody>
        </table>
        {darf && (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <input className={`${inlineInput} !w-56`} placeholder="Neues Schema, z. B. Montage auswärts" value={neu} onChange={(e) => setNeu(e.target.value)} />
            <button
              type="button"
              className={knopfPrimaer}
              disabled={!neu.trim()}
              onClick={() =>
                void schritt(async () => {
                  const s = await createScheme({ name: neu.trim(), copyFromId: auswahl ?? undefined })
                  setNeu('')
                  setAuswahl(s.id)
                })
              }
            >
              Anlegen
            </button>
            {schema && <span className="text-xs text-muted-foreground">als Kopie von „{schema.name}"</span>}
          </div>
        )}
      </div>

      {schema && (
        <div className={formCardSm}>
          <h3 className={cardTitle}>Zeilen von „{schema.name}"</h3>
          <p className="mt-1 text-xs text-muted-foreground">direkt = auf die Einzelkosten der Kostenart · laufend = auf den Wert bis zu dieser Zeile. Die Reihenfolge zählt.</p>
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className={tabellenKopf}>
                <th className="py-1.5 pr-2">Bezeichnung</th>
                {kosten && <th className="py-1.5 pr-2 text-right">%</th>}
                <th className="py-1.5 pr-2">gilt für</th>
                <th className="py-1.5 pr-2">Basis</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {schema.rows.map((r, i) => (
                <SchemaZeileEintrag
                  key={r.id}
                  schemeId={schema.id}
                  r={r}
                  darf={darf}
                  kosten={kosten}
                  erste={i === 0}
                  letzte={i === schema.rows.length - 1}
                  onFehler={setFehler}
                  onGeaendert={() => void laden()}
                  onSchieben={(um) => {
                    const ids = schema.rows.map((x) => x.id)
                    const [x] = ids.splice(i, 1)
                    ids.splice(i + um, 0, x)
                    void schritt(() => reorderSchemeRows(schema.id, ids))
                  }}
                  onLoeschen={async () => {
                    if (await bestaetige(`Zeile „${r.name}" löschen?`, { ok: 'Löschen', gefaehrlich: true })) void schritt(() => deleteSchemeRow(schema.id, r.id))
                  }}
                />
              ))}
            </tbody>
          </table>
          {darf && (
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <input className={`${inlineInput} !w-56`} placeholder="Neue Zeile, z. B. Wagnis" value={neueZeile} onChange={(e) => setNeueZeile(e.target.value)} />
              <button
                type="button"
                className={knopfPrimaer}
                disabled={!neueZeile.trim()}
                onClick={() =>
                  void schritt(async () => {
                    await createSchemeRow(schema.id, { name: neueZeile.trim(), percent: 0, appliesTo: ['MATERIAL'] })
                    setNeueZeile('')
                  })
                }
              >
                Anlegen
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function SchemaZeile({
  s,
  darf,
  gewaehlt,
  onWaehlen,
  onVorgabe,
  onLoeschen,
}: {
  s: CalcScheme
  darf: boolean
  gewaehlt: boolean
  onWaehlen: () => void
  onVorgabe: () => void
  onLoeschen: () => void
}) {
  const stelle = useZeilenStelle('calcScheme', schemeUrl(s.id), s, s.name)
  return (
    <tr className={`border-b border-border/60 align-top ${gewaehlt ? 'bg-primary/5' : ''}`} onClick={onWaehlen}>
      <td className="py-1.5 pr-2">
        <ZellenFeld stelle={stelle} feld="name" label="Name" darf={darf} pflicht />
      </td>
      <td className="py-1.5 pr-2">
        <input type="radio" name="vorgabe-schema" checked={s.isDefault} disabled={!darf || s.isDefault} onChange={onVorgabe} aria-label={`${s.name} als Vorgabe`} />
      </td>
      <td className="py-1.5 pr-2">{s.isDefault ? <span className="text-xs text-muted-foreground">ja</span> : <ZellenHaken stelle={stelle} feld="isActive" label="aktiv" darf={darf} />}</td>
      <td className="py-1.5 text-right">
        {darf && !s.isDefault && (
          <button type="button" className="text-muted-foreground hover:text-destructive" title="löschen" onClick={onLoeschen}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </td>
    </tr>
  )
}

function SchemaZeileEintrag({
  schemeId,
  r,
  darf,
  kosten,
  erste,
  letzte,
  onFehler,
  onGeaendert,
  onSchieben,
  onLoeschen,
}: {
  schemeId: string
  r: CalcSchemeRow
  darf: boolean
  kosten: boolean
  erste: boolean
  letzte: boolean
  onFehler: (t: string | null) => void
  onGeaendert: () => void
  onSchieben: (um: number) => void
  onLoeschen: () => void
}) {
  const url = schemeRowUrl(schemeId, r.id)
  const stelle = useZeilenStelle('calcSchemeRow', url, r, r.name)
  const basis = useFeld<string>(stelle, 'basis', { art: 'auswahl', label: 'Basis', gesperrt: !darf })
  // „gilt für" ist eine Mehrfachauswahl ohne Eingabefeld: sofort gespeichert (AP-36 Fall 1, Auswahl).
  const kostenart = async (k: (typeof KOSTENARTEN)[number]) => {
    const an = r.appliesTo.includes(k)
    const neu = an ? r.appliesTo.filter((x) => x !== k) : [...r.appliesTo, k]
    if (!neu.length) return
    try {
      onFehler(null)
      await api.patch(url, { appliesTo: neu }, mitStand(r.updatedAt))
      onGeaendert()
    } catch (e) {
      onFehler(apiErrorText(e, FEHLER))
    }
  }
  return (
    <tr className="border-b border-border/60 align-top">
      <td className="py-1.5 pr-2">
        <ZellenFeld stelle={stelle} feld="name" label="Bezeichnung" darf={darf} pflicht />
      </td>
      {kosten && (
        <td className="py-1.5 pr-2">
          <ZellenFeld stelle={stelle} feld="percent" label="Prozent" darf={darf} klasse="w-20 text-right" pflicht zahl />
        </td>
      )}
      <td className="py-1.5 pr-2">
        <span className="flex gap-0.5">
          {KOSTENARTEN.map((k) => (
            <button
              key={k}
              type="button"
              disabled={!darf}
              title={KOSTENART_TEXT[k]}
              className={`h-6 w-6 rounded text-xs ${r.appliesTo.includes(k) ? 'bg-primary text-primary-foreground' : 'border border-border text-muted-foreground'}`}
              onClick={() => void kostenart(k)}
            >
              {KOSTENART_KURZ[k]}
            </button>
          ))}
        </span>
      </td>
      <td className="py-1.5 pr-2">
        <select className={`${inlineInput} !w-28`} {...basis.props}>
          <option value="DIRECT">direkt</option>
          <option value="RUNNING">laufend</option>
        </select>
        <FeldStatus status={basis} />
      </td>
      <td className="whitespace-nowrap py-1.5 text-right">
        {darf && (
          <>
            <button type="button" className="text-muted-foreground disabled:opacity-30" disabled={erste} onClick={() => onSchieben(-1)} aria-label="nach oben">
              <ArrowUp className="h-4 w-4" />
            </button>
            <button type="button" className="text-muted-foreground disabled:opacity-30" disabled={letzte} onClick={() => onSchieben(1)} aria-label="nach unten">
              <ArrowDown className="h-4 w-4" />
            </button>
            <button type="button" className="ml-1 text-muted-foreground hover:text-destructive" title="löschen" onClick={onLoeschen}>
              <Trash2 className="h-4 w-4" />
            </button>
          </>
        )}
      </td>
    </tr>
  )
}
